import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../services/api';
import { getInsights, getStoreTaps } from '../services/marketingApi';
import { InsightsSection, InsightsSummary } from '../types/marketing';
import DateRangeSelect from '../components/marketing/DateRangeSelect';
import PlatformFilter, { StoreFilterToggle } from '../components/marketing/PlatformFilter';
import DataStamp from '../components/marketing/DataStamp';
import AlertBanner from '../components/marketing/AlertBanner';
import InsightsTabs, { InsightTab, resolveTab } from '../components/marketing/InsightsTabs';
import FunnelSection, { TapsState } from '../components/marketing/FunnelSection';
import AdsSection from '../components/marketing/AdsSection';
import ChannelsSection from '../components/marketing/ChannelsSection';
import SupplySection from '../components/marketing/SupplySection';
import RetentionSection from '../components/marketing/RetentionSection';
import AlertsSection from '../components/marketing/AlertsSection';
import SectionStatus from '../components/marketing/SectionStatus';
import StoreTapsView, { MarketingError, MarketingForbidden, MarketingSkeleton, StoreTapsState } from '../components/marketing/StoreTapsView';
import { useInsightsSections } from '../components/marketing/useInsightsSections';
import { resolvePlatform, resolveRange, resolveStore } from '../components/marketing/marketingMath';

type InsightsState =
  | { status: 'loading' }
  | { status: 'on'; data: InsightsSummary }
  | { status: 'off' }
  | { status: 'unavailable' }
  | { status: 'forbidden' }
  | { status: 'error'; message: string };

const errMessage = (err: unknown) => (err instanceof Error ? err.message : 'Failed');

/**
 * Spec 078 + 084 — Marketing. `/store-taps` (Spec 078) and `/insights` (Spec 084) load side by side. Insights
 * 200 → data stamp, alert banner and six tabs; 404 (switch `garagat.marketing.insights.enabled` off) → the Spec 078
 * view alone and no tab endpoint is called; 503 → "Numbers unavailable, try again"; 403 → "You don't have access".
 * Range, store, platform and tab live in the URL (`?from=&to=&store=&platform=&tab=`, replace).
 */
export default function MarketingPage() {
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const { from, to } = resolveRange(params.get('from'), params.get('to'));
  const store = resolveStore(params.get('store'));
  const platform = resolvePlatform(params.get('platform'));
  const tab = resolveTab(params.get('tab'));
  const [taps, setTaps] = useState<StoreTapsState>({ status: 'loading' });
  const [insights, setInsights] = useState<InsightsState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [tapsAttempt, setTapsAttempt] = useState(0);
  const sections = useInsightsSections(token, from, to, platform, insights.status === 'on');

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    setTaps({ status: 'loading' });
    getStoreTaps(token, from, to, store, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setTaps({ status: 'ok', data }); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.status === 403) setTaps({ status: 'forbidden' });
        else if (err instanceof ApiError && err.status === 503) setTaps({ status: 'unavailable' });
        else setTaps({ status: 'error', message: errMessage(err) });
      });
    return () => controller.abort();
  }, [token, from, to, store, attempt, tapsAttempt]);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    setInsights((s) => (s.status === 'off' ? s : { status: 'loading' }));
    getInsights(token, from, to, platform, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setInsights({ status: 'on', data }); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        const status = err instanceof ApiError ? err.status : 0;
        // 404 FEATURE_OFF (or a platform without the endpoint yet) → the Spec 078 page, unchanged.
        if (status === 404) setInsights({ status: 'off' });
        else if (status === 403) setInsights({ status: 'forbidden' });
        else if (status === 503) setInsights({ status: 'unavailable' });
        else setInsights({ status: 'error', message: errMessage(err) });
      });
    return () => controller.abort();
  }, [token, from, to, platform, attempt]);

  const { load, get } = sections;
  useEffect(() => {
    if (insights.status === 'on' && tab !== 'funnel') load(tab);
  }, [insights.status, tab, load]);

  const update = useCallback((patch: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      return next;
    }, { replace: true });
  }, [setParams]);
  const setRange = useCallback((r: { from: string; to: string }) => update({ from: r.from, to: r.to }), [update]);
  const selectTab = useCallback((t: InsightTab) => update({ tab: t }), [update]);
  const retry = () => setAttempt((a) => a + 1);

  if (taps.status === 'forbidden' || insights.status === 'forbidden') return <MarketingForbidden />;

  const range = insights.status === 'on' ? { from: insights.data.from, to: insights.data.to, timezone: insights.data.timezone } : { from, to, timezone: 'Asia/Dubai' };
  const retryOf = (s: InsightsSection) => () => load(s, true);
  const tapsForFunnel: TapsState = taps.status === 'ok' || taps.status === 'loading' ? taps
    : { status: 'error', message: taps.status === 'error' ? taps.message : 'Numbers unavailable, try again' };
  const renderPanel = (summary: InsightsSummary) => (tab === 'funnel'
    ? <FunnelSection summary={summary} taps={tapsForFunnel} onRetryTaps={() => setTapsAttempt((a) => a + 1)} />
    : tab === 'ads' ? <SectionStatus state={get('ads')} onRetry={retryOf('ads')}>{(d) => <AdsSection data={d} range={range} />}</SectionStatus>
      : tab === 'channels' ? <SectionStatus state={get('channels')} onRetry={retryOf('channels')}>{(d) => <ChannelsSection data={d} range={range} />}</SectionStatus>
        : tab === 'supply' ? <SectionStatus state={get('supply')} onRetry={retryOf('supply')}>{(d) => <SupplySection data={d} range={range} />}</SectionStatus>
          : tab === 'retention' ? <SectionStatus state={get('retention')} onRetry={retryOf('retention')}>{(d) => <RetentionSection data={d} range={range} />}</SectionStatus>
            : <SectionStatus state={get('alerts')} onRetry={retryOf('alerts')}>{(d) => <AlertsSection data={d} range={range} />}</SectionStatus>);

  return (
    <div className="mkt-page">
      <div className="dash-v2__head">
        <h2 className="dash-v2__title">Marketing</h2>
        <div className="mkt-filters">
          <DateRangeSelect from={from} to={to} onChange={setRange} />
          <StoreFilterToggle value={store ?? ''} onChange={(v) => update({ store: v || null })} />
          {insights.status !== 'off' && <PlatformFilter value={platform} onChange={(v) => update({ platform: v === 'all' ? null : v })} />}
        </div>
      </div>
      {insights.status === 'loading' && <MarketingSkeleton />}
      {insights.status === 'off' && <StoreTapsView state={taps} onRetry={retry} />}
      {(insights.status === 'unavailable' || insights.status === 'error') && (
        <MarketingError unavailable={insights.status === 'unavailable'} message={insights.status === 'error' ? insights.message : undefined} onRetry={retry} />
      )}
      {insights.status === 'on' && (
        <div data-testid="insights-view">
          <DataStamp dataAsOf={insights.data.dataAsOf} />
          <AlertBanner count={insights.data.alertsOpen} onOpen={() => selectTab('alerts')} />
          <InsightsTabs active={tab} onSelect={selectTab}>{renderPanel(insights.data)}</InsightsTabs>
        </div>
      )}
    </div>
  );
}
